import { Component, type ErrorInfo, type ReactNode } from "react";
import { useRouteError } from "react-router-dom";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level render-error safety net. React error boundaries must be class
 * components (no hook equivalent exists). Catches otherwise-fatal render
 * throws so the SPA shows a recoverable screen instead of a blank white page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Dashboard crashed:", error, info.componentStack);
  }

  override render() {
    if (this.state.error) return <CrashScreen onReload={() => window.location.reload()} />;
    return this.props.children;
  }
}

/** Per-route fallback for react-router's `errorElement` (data-router errors, not just render throws). */
export function RouteErrorBoundary() {
  const error = useRouteError();
  console.error("Route error:", error);
  return <CrashScreen onReload={() => window.location.reload()} />;
}

function CrashScreen({ onReload }: { onReload: () => void }) {
  return (
    <div style={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <h1 style={{ fontSize: 20, marginBottom: 8 }}>Something went wrong</h1>
        <p style={{ color: "var(--text-muted, #666)", marginBottom: 16 }}>
          An unexpected error occurred. Reloading usually fixes it.
        </p>
        <button onClick={onReload} style={{ padding: "8px 16px", cursor: "pointer" }}>
          Reload page
        </button>
      </div>
    </div>
  );
}
